<script setup lang="ts">
import {computed, ref} from "vue";
import LegacyAccess from "@/core/interfaces/Access";
import {jobRoleForRank, jobRoleOption} from "@/team/planning";
import {initialsOf} from "@/team-task/taskBoard";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {Check} from "@lucide/vue";

const {members} = defineProps<{ members: LegacyAccess[] }>();

const authorId = defineModel<number | null>({required: true});

const open = ref<boolean>(false);

/** The job colour doubles as the member's identity on screen. */
const colorOf = (member: LegacyAccess): string =>
    jobRoleOption(jobRoleForRank(member.rankId))?.color ?? '#6b7280';

const roleLabel = (member: LegacyAccess): string =>
    member.rank?.title ?? jobRoleOption(jobRoleForRank(member.rankId))?.label ?? '';

const selected = computed(() => members.find(member => member.id === authorId.value) ?? null);

const pick = (member: LegacyAccess) => {
    authorId.value = member.id;
    open.value = false;
};
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <button
        class="flex size-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition hover:ring-2 hover:ring-primary/20"
        :style="selected
          ? {backgroundColor: `${colorOf(selected)}1a`, color: colorOf(selected)}
          : undefined"
        :class="{'bg-gray-100 text-gray-400': !selected}"
        title="Choisir qui rédige"
      >
        {{ selected ? initialsOf(selected.user?.name) : '?' }}
      </button>
    </PopoverTrigger>

    <PopoverContent align="start" side="top" class="w-64 p-0">
      <p class="px-3 pb-1.5 pt-2.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
        Commenter en tant que
      </p>

      <ul class="max-h-56 overflow-y-auto">
        <li v-for="member in members" :key="member.id">
          <button
            class="flex w-full items-center gap-2.5 px-3 py-2 text-left transition"
            :class="member.id === authorId ? 'bg-primary/5' : 'hover:bg-gray-50'"
            @click="pick(member)"
          >
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
              :style="{backgroundColor: `${colorOf(member)}1a`, color: colorOf(member)}"
            >
              {{ initialsOf(member.user?.name) }}
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-xs font-semibold text-gray-900">{{ member.user?.name }}</span>
              <span class="block truncate text-[11px] text-gray-500">{{ roleLabel(member) }}</span>
            </span>
            <Check v-if="member.id === authorId" class="size-4 shrink-0 text-primary" />
          </button>
        </li>
      </ul>
    </PopoverContent>
  </Popover>
</template>
